import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgetPasswordForm from './ForgetPasswordForm';

const Modal = ({ show, onClose }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [isForgetPassword, setIsForgetPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Handle smooth modal animations
  useEffect(() => {
    if (show) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (show) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [show, onClose]);

  const handleToggle = () => {
    setIsRegister(!isRegister);
    setIsForgetPassword(false);
  };

  const handleToggleSetPass = () => {
    setIsRegister(false);
    setIsForgetPassword(!isForgetPassword);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-200 ${
        show 
          ? 'bg-black/50 backdrop-blur-sm opacity-100' 
          : 'bg-black/0 backdrop-blur-none opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`relative w-full max-w-md transform transition-all duration-200 ${
          show 
            ? 'translate-y-0 scale-100 opacity-100' 
            : 'translate-y-8 scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Modal Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with close button */}
          <div className="relative px-6 pt-6 pb-2">
            <button
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 group"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg 
                className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content Area */}
          <div className="px-6 pb-6">
            {/* Progress indicator */}
            <div className="flex justify-center mb-6">
              <div className="flex space-x-2">
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  !isRegister && !isForgetPassword ? 'bg-[#10c9c0] scale-125' : 'bg-gray-200'
                }`} />
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  isRegister ? 'bg-[#10c9c0] scale-125' : 'bg-gray-200'
                }`} />
                <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  isForgetPassword ? 'bg-[#10c9c0] scale-125' : 'bg-gray-200'
                }`} />
              </div>
            </div>

            {/* Form Container with slide animation */}
            <div className="relative overflow-hidden">
              <div className={`transition-all duration-300 ease-in-out ${
                isForgetPassword 
                  ? 'transform translate-x-0 opacity-100' 
                  : 'transform translate-x-full opacity-0 absolute inset-0'
              }`}>
                {isForgetPassword && (
                  <ForgetPasswordForm onToggle={handleToggleSetPass} />
                )}
              </div>

              <div className={`transition-all duration-300 ease-in-out ${
                isRegister && !isForgetPassword
                  ? 'transform translate-x-0 opacity-100' 
                  : 'transform translate-x-full opacity-0 absolute inset-0'
              }`}>
                {isRegister && !isForgetPassword && (
                  <RegisterForm onToggle={handleToggle} />
                )}
              </div>

              <div className={`transition-all duration-300 ease-in-out ${
                !isRegister && !isForgetPassword
                  ? 'transform translate-x-0 opacity-100' 
                  : 'transform -translate-x-full opacity-0 absolute inset-0'
              }`}>
                {!isRegister && !isForgetPassword && (
                  <LoginForm onToggle={handleToggle} onForgetPassword={handleToggleSetPass} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#10c9c0]/10 to-[#0eb1a9]/10 -z-10 blur-xl scale-110" />
      </div>
    </div>
  );
};

export default Modal;